import * as React from 'react'
import { format } from 'date-fns'
import { Search, Car, Calendar, TrendingUp, FileText, CheckCircle2, Download, Wallet, Coins } from 'lucide-react'
import { driverApi } from '@/api/drivers'
import { getOrders, updateOrder } from '@/api/orders'
import type { Driver } from '@/types'
import type { Order } from '@/api/orders'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

type PaidFilter = 'all' | 'unpaid' | 'paid'

export default function DriverReportPage() {
  const [drivers, setDrivers] = React.useState<Driver[]>([])
  const [selectedDriverId, setSelectedDriverId] = React.useState('')
  const [paidFilter, setPaidFilter] = React.useState<PaidFilter>('all')
  const [dateFrom, setDateFrom] = React.useState(() => {
    const d = new Date()
    d.setDate(1)
    return format(d, 'yyyy-MM-dd')
  })
  const [dateTo, setDateTo] = React.useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [orders, setOrders] = React.useState<Order[]>([])
  const [loading, setLoading] = React.useState(false)
  const [searched, setSearched] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [payTargets, setPayTargets] = React.useState<Order[] | null>(null)
  const [paySubmitting, setPaySubmitting] = React.useState(false)

  React.useEffect(() => {
    driverApi.getDrivers(1, 100).then(res => setDrivers(res.items || []))
  }, [])

  const handleSearch = async () => {
    if (!selectedDriverId) return
    setLoading(true)
    try {
      const res = await getOrders({
        limit: 1000,
        driver_id: selectedDriverId,
        date_from: dateFrom,
        date_to: dateTo,
      })
      setOrders(res.data)
      setSearched(true)
      setSelectedIds([])
    } finally {
      setLoading(false)
    }
  }

  // 標記「已付款給司機」——跟客戶的付款方式（payment_method）無關，是車行付給司機的司機費用
  // 點單筆或「批次付款」都先開確認視窗（payTargets），使用者按確認才真的送出
  const handleConfirmPay = async () => {
    if (!payTargets || payTargets.length === 0) return
    setPaySubmitting(true)
    try {
      const paidAt = new Date().toISOString()
      const results = await Promise.all(
        payTargets.map(order => updateOrder(order.id, { driver_paid_at: paidAt }).catch(() => null))
      )
      const updatedMap = new Map<string, string>()
      payTargets.forEach((order, i) => {
        const paidValue = results[i]?.driver_paid_at
        if (paidValue) updatedMap.set(order.id, paidValue)
      })
      setOrders(prev => prev.map(o => updatedMap.has(o.id) ? { ...o, driver_paid_at: updatedMap.get(o.id) } : o))
      setSelectedIds(prev => prev.filter(id => !updatedMap.has(id)))
      setPayTargets(null)
    } finally {
      setPaySubmitting(false)
    }
  }

  const selectedDriver = drivers.find(d => d.id === selectedDriverId)

  const filteredOrders = orders.filter(o => {
    const isPaid = !!o.driver_paid_at
    if (paidFilter === 'unpaid' && isPaid) return false
    if (paidFilter === 'paid' && !isPaid) return false
    return true
  })

  const unpaidOrders = filteredOrders.filter(o => !o.driver_paid_at && o.payment_method !== '現金')
  const allUnpaidSelected = unpaidOrders.length > 0 && unpaidOrders.every(o => selectedIds.includes(o.id))
  const selectedOrders = filteredOrders.filter(o => selectedIds.includes(o.id))
  const selectedFeeTotal = Math.round(selectedOrders.reduce((sum, o) => sum + (o.pricing?.driver_fee || 0), 0))

  const toggleSelectAll = () => {
    setSelectedIds(allUnpaidSelected ? [] : unpaidOrders.map(o => o.id))
  }

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const totalDriverFee = Math.round(filteredOrders.reduce((sum, o) => sum + (o.pricing?.driver_fee || 0), 0))
  const totalAmount = Math.round(filteredOrders.reduce((sum, o) => sum + (o.pricing?.total_amount || 0), 0))
  const unpaidDriverFee = Math.round(filteredOrders.filter(o => !o.driver_paid_at && o.payment_method !== '現金').reduce((sum, o) => sum + (o.pricing?.driver_fee || 0), 0))
  // 回金 = 廠商回金（vendor_rebate）
  const rebateOf = (o: Order) => o.pricing?.vendor_rebate || 0
  const totalRebate = Math.round(filteredOrders.reduce((sum, o) => sum + rebateOf(o), 0))
  const paidDriverFee = Math.round(filteredOrders.filter(o => !!o.driver_paid_at).reduce((sum, o) => sum + (o.pricing?.driver_fee || 0), 0))

  const statusColor: Record<string, string> = {
    '待派車': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    '已派車': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    '進行中': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    '已完成': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    '已取消': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  const handleExportExcel = async () => {
    const { utils, writeFile } = await import('xlsx')
    const headers = ['#', '日期時間', '乘客', '行程', '狀態', '付款方式', '車資', '司機費用', '回金', '司機款項付款日期']
    const rows = filteredOrders.map((order, idx) => [
      idx + 1,
      order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-',
      order.passenger_name || '',
      `${order.pickup_location || ''} → ${order.dropoff_location || ''}`,
      order.status,
      order.payment_method || '',
      Math.round(order.pricing?.total_amount || 0),
      Math.round(order.pricing?.driver_fee || 0),
      Math.round(rebateOf(order)),
      order.driver_paid_at ? format(new Date(order.driver_paid_at), 'yyyy-MM-dd') : '未付款',
    ])
    const wsData = [
      [`司機報表 - ${selectedDriver?.name}`],
      [`期間：${dateFrom} ～ ${dateTo}`],
      [],
      headers,
      ...rows,
      [],
      ['', '', '', '', '', '', '合計', totalDriverFee, totalRebate, ''],
      ['', '', '', '', '', '', '', '未付款', unpaidDriverFee],
      ['', '', '', '', '', '', '', '已付款', paidDriverFee],
    ]
    const ws = utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 4 }, { wch: 12 }, { wch: 12 }, { wch: 34 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }]
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, '司機報表')
    writeFile(wb, `司機報表_${selectedDriver?.name}_${dateFrom}_${dateTo}.xlsx`)
  }

  const handleExportPDF = () => {
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;')
    const sorted = [...filteredOrders].sort((a, b) => (a.pickup_datetime || '').localeCompare(b.pickup_datetime || ''))
    const rows = sorted.map((o, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${o.pickup_datetime ? format(new Date(o.pickup_datetime), 'MM/dd HH:mm') : '-'}</td>
        <td>${esc(o.passenger_name || '')}</td>
        <td>${esc(o.pickup_location || '')} → ${esc(o.dropoff_location || '')}</td>
        <td>${esc(o.payment_method || '')}</td>
        <td style="text-align:right">$${Math.round(o.pricing?.total_amount || 0).toLocaleString()}</td>
        <td style="text-align:right">$${Math.round(o.pricing?.driver_fee || 0).toLocaleString()}</td>
        <td style="text-align:right">$${Math.round(rebateOf(o)).toLocaleString()}</td>
        <td>${o.driver_paid_at ? format(new Date(o.driver_paid_at), 'MM/dd') : '未付款'}</td>
      </tr>`).join('')
    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>司機報表</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: "Microsoft JhengHei", "PingFang TC", sans-serif; font-size: 11px; color: #111; }
  h2 { font-size: 16px; margin: 0 0 4px; }
  p { margin: 0 0 10px; color: #555; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #2563eb; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; }
  td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
  tr:nth-child(even) td { background: #f9fafb; }
  .total td { font-weight: bold; background: #eff6ff; border-top: 2px solid #2563eb; }
</style></head><body>
<h2>司機報表 - ${esc(selectedDriver?.name || '')}</h2>
<p>期間：${dateFrom} ～ ${dateTo}　共 ${sorted.length} 筆</p>
<table>
  <thead><tr><th>#</th><th>日期時間</th><th>乘客</th><th>行程</th><th>付款方式</th><th>車資</th><th>司機費用</th><th>回金</th><th>司機款項</th></tr></thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr class="total"><td colspan="5">合計</td><td style="text-align:right">$${totalAmount.toLocaleString()}</td><td style="text-align:right">$${totalDriverFee.toLocaleString()}</td><td style="text-align:right">$${totalRebate.toLocaleString()}</td><td></td></tr>
    <tr class="total"><td colspan="6"></td><td>未付款</td><td style="text-align:right">$${unpaidDriverFee.toLocaleString()}</td><td></td><td></td></tr>
    <tr class="total"><td colspan="6"></td><td>已付款</td><td style="text-align:right">$${paidDriverFee.toLocaleString()}</td><td></td><td></td></tr>
  </tfoot>
</table></body></html>`
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(html)
    win.document.close()
    win.onload = () => { win.print() }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
          <Car className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">司機報表</h1>
      </div>

      {/* 篩選條件 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px] space-y-1">
            <label className="text-xs font-medium text-gray-500">司機</label>
            <select
              value={selectedDriverId}
              onChange={e => setSelectedDriverId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer"
            >
              <option value="">選擇司機</option>
              {drivers.map(d => (
                <option key={d.id} value={d.id}>{d.name} - {d.vehicle_plate}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[140px] space-y-1">
            <label className="text-xs font-medium text-gray-500">司機款項狀態</label>
            <select
              value={paidFilter}
              onChange={e => setPaidFilter(e.target.value as PaidFilter)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer"
            >
              <option value="all">全部</option>
              <option value="unpaid">未付款</option>
              <option value="paid">已付款</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />快速選擇</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const now = new Date()
                  setDateFrom(format(new Date(now.getFullYear(), now.getMonth() - 1, 1), 'yyyy-MM-dd'))
                  setDateTo(format(new Date(now.getFullYear(), now.getMonth(), 0), 'yyyy-MM-dd'))
                }}
                className="h-10 px-4 rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 text-sm font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors cursor-pointer"
              >
                上個月
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date()
                  setDateFrom(format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'))
                  setDateTo(format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd'))
                }}
                className="h-10 px-4 rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 text-sm font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors cursor-pointer"
              >
                本月
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />開始日期</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />結束日期</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={!selectedDriverId || loading}
            className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold"
          >
            <Search className="h-4 w-4 mr-2" />
            {loading ? '查詢中...' : '查詢'}
          </Button>
        </div>
      </div>

      {/* 統計卡片 */}
      {searched && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><FileText className="h-4 w-4" />訂單數</div>
            <div className="text-2xl font-black text-gray-900 dark:text-white">{filteredOrders.length} 筆</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />車資合計</div>
            <div className="text-2xl font-black text-gray-700 dark:text-gray-300">${totalAmount.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Coins className="h-4 w-4" />回金合計</div>
            <div className="text-2xl font-black text-purple-600 dark:text-purple-400">${totalRebate.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />應付司機款（未付款）</div>
            <div className="text-2xl font-black text-red-600 dark:text-red-400">${unpaidDriverFee.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><CheckCircle2 className="h-4 w-4" />已付司機款總額</div>
            <div className="text-2xl font-black text-green-600 dark:text-green-400">${paidDriverFee.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* 訂單列表 */}
      {searched && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
            <span className="font-bold text-gray-900 dark:text-white text-sm">
              {selectedDriver?.name} 的訂單明細
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{dateFrom} ～ {dateTo}</span>
              {selectedIds.length > 0 && (
                <Button
                  onClick={() => setPayTargets(selectedOrders)}
                  className="h-8 px-3 text-xs bg-amber-500 hover:bg-amber-600 text-white font-bold"
                >
                  <Wallet className="h-3 w-3 mr-1" />
                  付款給司機（已選 {selectedIds.length} 筆）
                </Button>
              )}
              {filteredOrders.length > 0 && (
                <Button
                  onClick={handleExportExcel}
                  variant="outline"
                  className="h-8 px-3 text-xs border-green-300 text-green-700 hover:bg-green-50"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Excel
                </Button>
              )}
              {filteredOrders.length > 0 && (
                <Button
                  onClick={handleExportPDF}
                  variant="outline"
                  className="h-8 px-3 text-xs border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Download className="h-3 w-3 mr-1" />
                  PDF
                </Button>
              )}
            </div>
          </div>
          {filteredOrders.length === 0 ? (
            <div className="py-16 text-center text-gray-400">此條件無訂單資料</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      {unpaidOrders.length > 0 && (
                        <Checkbox checked={allUnpaidSelected} onCheckedChange={toggleSelectAll} />
                      )}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">日期時間</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">乘客</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">行程</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">狀態</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">付款方式</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">車資</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase font-black text-blue-600">司機費用</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-purple-600 uppercase font-black">回金</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">司機款項</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredOrders.map((order, idx) => (
                    <tr key={order.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-slate-50 dark:bg-gray-750'}>
                      <td className="px-4 py-3">
                        {!order.driver_paid_at && order.payment_method !== '現金' && (
                          <Checkbox checked={selectedIds.includes(order.id)} onCheckedChange={() => toggleSelectOne(order.id)} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{order.passenger_name}</div>
                        <div className="text-xs text-gray-400">{order.passenger_phone}</div>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <div className="text-xs text-gray-500 truncate">{order.pickup_location}</div>
                        <div className="text-xs text-gray-500 truncate">→ {order.dropoff_location}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusColor[order.status] || 'bg-gray-100 text-gray-600'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {order.payment_method || '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        ${Math.round(order.pricing?.total_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-black text-blue-600 dark:text-blue-400">
                        ${Math.round(order.pricing?.driver_fee || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-purple-600 dark:text-purple-400">
                        ${Math.round(rebateOf(order)).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {order.driver_paid_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <CheckCircle2 className="h-3 w-3" />
                            {format(new Date(order.driver_paid_at), 'MM/dd')}
                          </span>
                        ) : order.payment_method !== '現金' ? (
                          <button
                            type="button"
                            onClick={() => setPayTargets([order])}
                            className="px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors cursor-pointer"
                          >
                            付款給司機
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">現金免付</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-blue-50 dark:bg-blue-900/20 border-t-2 border-blue-200 dark:border-blue-700">
                  <tr>
                    <td colSpan={7} className="px-4 py-3 font-bold text-gray-700 dark:text-gray-300">合計</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-700 dark:text-gray-300">
                      ${totalAmount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-black text-blue-600 dark:text-blue-400 text-base">
                      ${totalDriverFee.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-black text-purple-600 dark:text-purple-400 text-base">
                      ${totalRebate.toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!payTargets && payTargets.length > 0}
        title="付款給司機"
        message={
          payTargets && payTargets.length === 1
            ? `確認已將司機費用 $${Math.round(payTargets[0].pricing?.driver_fee || 0).toLocaleString()} 付給 ${payTargets[0].driver_name || '司機'} 嗎？`
            : payTargets
              ? `確認已將 ${payTargets.length} 筆訂單、共 $${selectedFeeTotal.toLocaleString()} 的司機費用付給 ${selectedDriver?.name || '司機'} 嗎？`
              : ''
        }
        confirmText="確認已付款"
        loading={paySubmitting}
        onConfirm={handleConfirmPay}
        onCancel={() => setPayTargets(null)}
      />
    </div>
  )
}
